import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export interface AudioState {
  currentSrc: string | null
  isPlaying: boolean
  isLoading: boolean
  currentTime: number
  duration: number
  muted: boolean
  loop: boolean
  rate: number
  endCount: number
  seekVersion: number
}

interface AudioActions {
  // Playback controls
  play: (src: string, startTime?: number) => void
  pause: () => void
  stop: () => void
  seek: (time: number) => void

  // Internal state updates (used by AudioPlayer component)
  setIsPlaying: (isPlaying: boolean) => void
  setIsLoading: (isLoading: boolean) => void
  setCurrentTime: (time: number) => void
  setDuration: (duration: number) => void
  setCurrentSrc: (src: string | null) => void
  setPlaybackOptions: (options: { muted: boolean; loop: boolean; rate: number }) => void
  markEnded: () => void

  // Cleanup
  cleanup: () => void
}

export type AudioStore = AudioState & AudioActions

export const useAudioStore = create<AudioStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentSrc: null,
      isPlaying: false,
      isLoading: false,
      currentTime: 0,
      duration: 0,
      muted: false,
      loop: false,
      rate: 1,
      endCount: 0,
      seekVersion: 0,

      // Actions
      play: (src, startTime) => {
        const { currentSrc } = get()

        // If already playing this source, do nothing
        if (currentSrc === src) {
          set({
            isPlaying: true,
            ...(startTime !== undefined ? { currentTime: startTime, seekVersion: get().seekVersion + 1 } : {}),
          })
          return
        }

        set({
          currentSrc: src,
          isPlaying: true,
          isLoading: true,
          currentTime: startTime !== undefined ? startTime : 0,
          duration: 0,
          seekVersion: get().seekVersion + 1,
        })
      },

      pause: () => {
        const { isPlaying } = get()
        if (isPlaying) {
          set({ isPlaying: false })
        }
      },

      stop: () => {
        set({
          currentSrc: null,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
        })
      },

      seek: (time) => {
        const { duration } = get()

        // Clamp the time between 0 and duration
        const clampedTime = Math.max(0, duration > 0 ? Math.min(time, duration) : time)

        set({ currentTime: clampedTime, seekVersion: get().seekVersion + 1 })
      },

      // Internal state updates
      setIsPlaying: (isPlaying) => set({ isPlaying }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setCurrentTime: (currentTime) => set({ currentTime }),
      setDuration: (duration) => set({ duration }),
      setCurrentSrc: (currentSrc) => set({ currentSrc }),
      setPlaybackOptions: (options) => set(options),
      markEnded: () => set((state) => ({ endCount: state.endCount + 1 })),

      // Cleanup
      cleanup: () => {
        set({
          currentSrc: null,
          isPlaying: false,
          isLoading: false,
          currentTime: 0,
          duration: 0,
        })
      },
    }),
    { name: 'audio-store' }
  )
)
